
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MessageSquare, Search, Sparkles } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { TherapistProps } from '@/components/therapists/TherapistCard';
import { therapistsData } from '@/data/therapists';
import { useLanguage } from '@/context/LanguageContext';

interface AITherapistMatch {
  therapistId: string;
  matchScore: number;
  reasonForMatch: string;
}

const TherapistMatcher: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionType, setSessionType] = useState<string>('both');
  const [priceRange, setPriceRange] = useState<string>('any');
  const [includeSymptoms, setIncludeSymptoms] = useState(true);
  const [includePreferences, setIncludePreferences] = useState(true);
  const [matches, setMatches] = useState<AITherapistMatch[]>([]);
  
  // This would be a real API call in production
  const findTherapistMatches = () => {
    setIsLoading(true);
    
    // Simulate API delay
    setTimeout(() => {
      // Simple mock matching algorithm - in production this would be AI-based
      const matchedTherapists = therapistsData
        .filter(therapist => {
          // Filter by session type
          if (sessionType === 'online' && !therapist.isOnline) return false;
          if (sessionType === 'inperson' && therapist.isOnline) return false;
          
          // Filter by price range
          if (priceRange === 'under50' && therapist.price > 50) return false;
          if (priceRange === 'under100' && therapist.price > 100) return false;
          if (priceRange === 'under150' && therapist.price > 150) return false;
          
          // Check if description contains any of the therapist's specialties
          if (description) {
            const descriptionLower = description.toLowerCase();
            return therapist.specialties.some(specialty => 
              descriptionLower.includes(specialty.toLowerCase())
            );
          }
          
          return true;
        })
        .slice(0, 3) // Limit to 3 matches
        .map(therapist => {
          // Calculate a mock "match score" between 70 and 100
          const matchScore = Math.floor(Math.random() * 31) + 70;
          
          return {
            therapistId: therapist.id,
            matchScore,
            reasonForMatch: `${therapist.name} specializes in ${therapist.specialties.join(', ')}, which aligns with your needs.`
          };
        });
      
      setMatches(matchedTherapists);
      setIsLoading(false);
    }, 2000);
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    findTherapistMatches();
  };
  
  const getTherapistById = (id: string): TherapistProps | undefined => {
    return therapistsData.find(t => t.id === id);
  };
  
  return (
    <div className="max-w-3xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Sparkles className="h-5 w-5 mr-2 text-primary" />
            {t('aiTherapistMatching')}
          </CardTitle>
          <CardDescription>
            {t('aiAssistantDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="description">{t('describeWhatYoureLookingFor')}</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your needs, concerns, or what you're looking for in a therapist. For example: 'I've been feeling anxious about work and having trouble sleeping. I'd prefer someone with experience in stress management.'"
                  className="min-h-[120px]"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="session-type">{t('sessionType')}</Label>
                  <Select value={sessionType} onValueChange={setSessionType}>
                    <SelectTrigger id="session-type">
                      <SelectValue placeholder={t('selectSessionType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">{t('bothOnlineInPerson')}</SelectItem>
                      <SelectItem value="online">{t('onlineOnly')}</SelectItem>
                      <SelectItem value="inperson">{t('inPersonOnly')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="price-range">{t('priceRange')}</Label>
                  <Select value={priceRange} onValueChange={setPriceRange}>
                    <SelectTrigger id="price-range">
                      <SelectValue placeholder={t('selectPriceRange')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{t('anyPrice')}</SelectItem>
                      <SelectItem value="under50">Under $50</SelectItem>
                      <SelectItem value="under100">Under $100</SelectItem>
                      <SelectItem value="under150">Under $150</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="symptoms" 
                    checked={includeSymptoms} 
                    onCheckedChange={(checked) => 
                      setIncludeSymptoms(checked as boolean)
                    }
                  />
                  <Label htmlFor="symptoms">{t('includeSymptomsMatching')}</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="preferences" 
                    checked={includePreferences} 
                    onCheckedChange={(checked) => 
                      setIncludePreferences(checked as boolean)
                    }
                  />
                  <Label htmlFor="preferences">{t('considerTherapistStyles')}</Label>
                </div>
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('findingYourMatches')}
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    {t('findMyTherapistMatch')}
                  </>
                )}
              </Button>
            </div>
          </form>
          
          {matches.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-medium mb-4">{t('yourTopMatches')}</h3>
              <div className="space-y-4">
                {matches.map((match) => {
                  const therapist = getTherapistById(match.therapistId);
                  if (!therapist) return null;
                  
                  return (
                    <Card key={match.therapistId}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-12 w-12">
                            {therapist.image ? (
                              <img src={therapist.image} alt={therapist.name} className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center bg-primary-foreground text-primary font-medium">
                                {therapist.name.charAt(0)}
                              </div>
                            )}
                          </Avatar>
                          
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-medium">{therapist.name}</h4>
                              <div className="bg-primary/10 text-primary px-2 py-1 rounded-full text-sm">
                                {match.matchScore}% {t('match')}
                              </div>
                            </div>
                            
                            <p className="text-sm text-muted-foreground mb-2">
                              {therapist.specialties.join(', ')}
                            </p>
                            
                            <p className="text-sm mb-3">
                              {match.reasonForMatch}
                            </p>
                            
                            <div className="flex gap-2">
                              <Button 
                                variant="default" 
                                size="sm"
                                onClick={() => navigate(`/therapist/${therapist.id}`)}
                              >
                                {t('viewProfile')}
                              </Button>
                              <Button variant="outline" size="sm">
                                <MessageSquare className="h-4 w-4 mr-1" />
                                {t('message')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TherapistMatcher;
